/**
 * CanvasRuntime.ts
 * Runtime pour les opérations courantes sur le canvas Konva
 * Encapsule les opérations de base du Stage de manière type-safe
 */
import React from 'react';
import Konva from 'konva';
import { logger } from '../../utils/ProfessionalLogger';

export interface CanvasRuntimeConfig {
  stageRef: React.RefObject<Konva.Stage | null>;
  templateWidth?: number;
  templateHeight?: number;
}

/**
 * Runtime pour les opérations sur le canvas
 * Fournit une API minimale et type-safe pour interagir avec le Stage Konva
 */
export class CanvasRuntime {
  private stageRef: React.RefObject<Konva.Stage | null>;
  private templateWidth: number;
  private templateHeight: number;

  constructor(config: CanvasRuntimeConfig) {
    this.stageRef = config.stageRef;
    this.templateWidth = config.templateWidth ?? 800;
    this.templateHeight = config.templateHeight ?? 600;
  }

  /**
   * Factory pour créer une instance de CanvasRuntime
   */
  static create(config: CanvasRuntimeConfig): CanvasRuntime {
    return new CanvasRuntime(config);
  }

  /**
   * Récupère le Stage Konva actuel
   * @returns Le Stage ou null si non disponible
   */
  public getStage(): Konva.Stage | null {
    return this.stageRef.current ?? null;
  }

  /**
   * Exporte le canvas en DataURL (base64)
   * @param pixelRatio Ratio de pixels pour l'export (défaut: 1)
   * @returns DataURL string ou null si le stage n'est pas disponible
   */
  public exportAsDataURL(pixelRatio: number = 1): string | null {
    const stage = this.getStage();
    if (!stage) {
      logger.warn('⚠️ CanvasRuntime.exportAsDataURL: Stage non disponible');
      return null;
    }

    try {
      const dataURL = stage.toDataURL({
        pixelRatio,
        mimeType: 'image/png',
        quality: 1,
      });
      return dataURL;
    } catch (error) {
      logger.error("Erreur lors de l'export en DataURL:", error);
      return null;
    }
  }

  /**
   * Exporte le canvas en PNG (via toDataURL)
   * @param pixelRatio Ratio de pixels pour l'export (défaut: 2)
   * @returns DataURL string (PNG) ou null si le stage n'est pas disponible
   */
  public exportPNG(pixelRatio: number = 2): string | null {
    const stage = this.getStage();
    if (!stage) {
      logger.warn('⚠️ CanvasRuntime.exportPNG: Stage non disponible');
      return null;
    }

    try {
      const dataURL = stage.toDataURL({
        pixelRatio,
        mimeType: 'image/png',
        quality: 1,
      });
      logger.info(`✅ PNG exporté avec pixelRatio=${pixelRatio}`);
      return dataURL;
    } catch (error) {
      logger.error("Erreur lors de l'export PNG:", error);
      return null;
    }
  }

  /**
   * Exporte le canvas en Blob
   * @param pixelRatio Ratio de pixels pour l'export (défaut: 1)
   * @returns Promise avec le Blob ou null si le stage n'est pas disponible
   */
  public async exportAsBlob(pixelRatio: number = 1): Promise<Blob | null> {
    const stage = this.getStage();
    if (!stage) {
      logger.warn('⚠️ CanvasRuntime.exportAsBlob: Stage non disponible');
      return null;
    }

    try {
      const dataURL = this.exportAsDataURL(pixelRatio);
      if (!dataURL) {
        return null;
      }

      // Convertir DataURL en Blob
      const response = await fetch(dataURL);
      const blob = await response.blob();
      return blob;
    } catch (error) {
      logger.error("Erreur lors de l'export en Blob:", error);
      return null;
    }
  }

  /**
   * Test de collision (hit test) à une position donnée
   * @param pointer Position du pointeur en coordonnées du stage
   * @returns Le nœud trouvé ou null
   */
  public hitTest(pointer: { x: number; y: number }): Konva.Node | null {
    const stage = this.getStage();
    if (!stage) {
      logger.warn('⚠️ CanvasRuntime.hitTest: Stage non disponible');
      return null;
    }

    try {
      // Konva utilise getIntersection pour le hit test
      const node = stage.getIntersection(pointer);
      return node ?? null;
    } catch (error) {
      logger.error('Erreur lors du hit test:', error);
      return null;
    }
  }

  /**
   * Ajuste le zoom pour que tout le canvas soit visible (fit to screen)
   * @param padding Padding en pixels autour du canvas (défaut: 50)
   */
  public fitToScreen(padding: number = 50): void {
    const stage = this.getStage();
    if (!stage) {
      logger.warn('⚠️ CanvasRuntime.fitToScreen: Stage non disponible');
      return;
    }

    try {
      const container = stage.container();
      if (!container) {
        logger.warn('⚠️ CanvasRuntime.fitToScreen: Container non disponible');
        return;
      }

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      if (containerWidth === 0 || containerHeight === 0) {
        logger.warn('⚠️ CanvasRuntime.fitToScreen: Dimensions du container invalides');
        return;
      }

      // Calculer le scale pour que le canvas + padding rentre dans le container
      const availableWidth = containerWidth - padding * 2;
      const availableHeight = containerHeight - padding * 2;

      const scaleX = availableWidth / this.templateWidth;
      const scaleY = availableHeight / this.templateHeight;
      const newScale = Math.min(scaleX, scaleY, 1); // Ne pas zoomer au-delà de 100%

      // Appliquer le scale
      stage.scale({ x: newScale, y: newScale });

      // Centrer le canvas
      const scaledWidth = this.templateWidth * newScale;
      const scaledHeight = this.templateHeight * newScale;
      const centeredX = (containerWidth - scaledWidth) / 2;
      const centeredY = (containerHeight - scaledHeight) / 2;

      stage.position({ x: centeredX, y: centeredY });
      stage.batchDraw();

      logger.info(
        `✅ Canvas ajusté: scale=${newScale.toFixed(2)}, position=(${centeredX.toFixed(0)}, ${centeredY.toFixed(0)})`
      );
    } catch (error) {
      logger.error('Erreur lors du fitToScreen:', error);
    }
  }

  /**
   * Centre la vue sur un nœud spécifique
   * @param nodeId ID du nœud à centrer
   */
  public centerOnNode(nodeId: string): void {
    const stage = this.getStage();
    if (!stage) {
      logger.warn('⚠️ CanvasRuntime.centerOnNode: Stage non disponible');
      return;
    }

    try {
      // Trouver le nœud par son ID
      const node = stage.findOne(`#${nodeId}`);
      if (!node) {
        logger.warn(`⚠️ CanvasRuntime.centerOnNode: Nœud "${nodeId}" non trouvé`);
        return;
      }

      const container = stage.container();
      if (!container) {
        logger.warn('⚠️ CanvasRuntime.centerOnNode: Container non disponible');
        return;
      }

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Obtenir la position absolue du nœud
      const nodePos = node.absolutePosition();
      const _nodeBox = node.getClientRect();

      // Calculer la position du stage pour centrer le nœud
      const scale = stage.scaleX();
      const centerX = containerWidth / 2;
      const centerY = containerHeight / 2;

      // Position du nœud dans les coordonnées du stage (avec scale)
      const nodeCenterX = nodePos.x * scale;
      const nodeCenterY = nodePos.y * scale;

      // Ajuster la position du stage pour centrer le nœud
      const newStageX = centerX - nodeCenterX;
      const newStageY = centerY - nodeCenterY;

      stage.position({ x: newStageX, y: newStageY });
      stage.batchDraw();

      logger.info(`✅ Vue centrée sur le nœud "${nodeId}"`);
    } catch (error) {
      logger.error('Erreur lors du centerOnNode:', error);
    }
  }
}

/**
 * Exporte les shapes du store en JSON
 * @param shapes Array de ShapeData depuis le store Zustand
 * @returns JSON stringifié des shapes
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function exportJSON(shapes: any[]): string {
  try {
    const json = JSON.stringify(shapes, null, 2);
    logger.info(`✅ JSON exporté: ${shapes.length} shape(s)`);
    return json;
  } catch (error) {
    logger.error("Erreur lors de l'export JSON:", error);
    throw error;
  }
}

/**
 * Interface pour le bundle d'export complet
 */
export interface CanvasExportBundle {
  pngDataUrl: string;
  json: string;
  meta: {
    width: number;
    height: number;
    scale: number;
    position: { x: number; y: number };
    shapeCount: number;
    exportedAt: string;
  };
}

/**
 * Exporte un bundle complet du canvas (PNG + JSON + métadonnées)
 * @param stageRef Référence au Stage Konva
 * @param shapes Shapes depuis le store Zustand
 * @param pixelRatio Ratio de pixels pour l'export PNG (défaut: 2)
 * @returns Bundle d'export complet ou null si erreur
 */
export function exportCanvasBundle(
  stageRef: React.RefObject<Konva.Stage | null>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  shapes: any[],
  pixelRatio: number = 2
): CanvasExportBundle | null {
  try {
    const stage = stageRef.current;
    if (!stage) {
      logger.warn('⚠️ exportCanvasBundle: Stage non disponible');
      return null;
    }

    // Export PNG
    const pngDataUrl = stage.toDataURL({
      pixelRatio,
      mimeType: 'image/png',
      quality: 1,
    });

    if (!pngDataUrl) {
      logger.warn("⚠️ exportCanvasBundle: Échec de l'export PNG");
      return null;
    }

    // Export JSON
    const json = exportJSON(shapes);

    // Métadonnées
    const scale = stage.scaleX();
    const position = stage.position();
    const container = stage.container();
    const width = container ? container.clientWidth : stage.width();
    const height = container ? container.clientHeight : stage.height();

    const bundle: CanvasExportBundle = {
      pngDataUrl,
      json,
      meta: {
        width,
        height,
        scale,
        position: { x: position.x, y: position.y },
        shapeCount: shapes.length,
        exportedAt: new Date().toISOString(),
      },
    };

    logger.info(`✅ Bundle d'export créé: ${shapes.length} shape(s), ${width}x${height}px`);
    return bundle;
  } catch (error) {
    logger.error("Erreur lors de l'export du bundle:", error);
    return null;
  }
}
